import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatPHP } from '../lib/utils';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Printer, TrendingUp, TrendingDown, ArrowRight, ExternalLink, GitBranch, LayoutGrid, List } from 'lucide-react';
import PrintEngine from '../lib/PrintEngine';
import BranchCapitalCompareModal from './BranchCapitalCompareModal';
import POBranchPropagateMatrix from './POBranchPropagateMatrix';

/**
 * POPriceChangeModal
 * Shown after a Purchase Order is fully received. Lists the products whose
 * capital cost moved in this PO, with each product's previous price points
 * (last 3, dated), and offers a printable Price Change Report + a link to the
 * full Capital Change Report page. Admins can also propagate a product's new
 * capital to other branches (cross-branch capital propagation).
 */
export default function POPriceChangeModal({ open, onOpenChange, report, businessInfo, sourceBranchId, canPropagate }) {
  const navigate = useNavigate();
  const [compareProduct, setCompareProduct] = useState(null);
  const [view, setView] = useState('list'); // 'list' | 'matrix'
  if (!report) return null;
  const products = report.products || [];

  const handlePrint = () => {
    PrintEngine.print({
      type: 'po_price_change_report',
      data: report,
      format: 'dot_matrix',
      businessInfo: businessInfo || {},
    });
  };

  const fmtDate = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch { return ''; }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={view === 'matrix' ? 'max-w-6xl' : 'max-w-2xl'} data-testid="po-price-change-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#1A4D2E]" style={{ fontFamily: 'Manrope' }}>
            <TrendingUp size={18} /> Price Changes in this PO
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            {products.length} product{products.length !== 1 ? 's' : ''} had their capital cost updated by{' '}
            <span className="font-mono font-semibold text-slate-700">{report.po_number}</span>
            {report.vendor ? ` (${report.vendor})` : ''}. Review retail prices where cost increased.
          </DialogDescription>
        </DialogHeader>

        {canPropagate && (
          <div className="flex items-center gap-1 -mt-1">
            <div className="inline-flex rounded-lg border p-0.5 bg-slate-50" style={{ borderColor: 'var(--border-default)' }}>
              <button onClick={() => setView('list')} data-testid="po-price-view-list"
                className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-md transition-colors ${view === 'list' ? 'bg-white shadow-sm text-[#1A4D2E] font-semibold' : 'text-slate-500 hover:text-slate-700'}`}>
                <List size={13} /> Changes
              </button>
              <button onClick={() => setView('matrix')} data-testid="po-price-view-matrix"
                className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-md transition-colors ${view === 'matrix' ? 'bg-white shadow-sm text-[#1A4D2E] font-semibold' : 'text-slate-500 hover:text-slate-700'}`}>
                <LayoutGrid size={13} /> Branch Matrix
              </button>
            </div>
          </div>
        )}

        {view === 'matrix' ? (
          <POBranchPropagateMatrix
            products={products}
            sourcePoNumber={report.po_number}
            sourceBranchId={sourceBranchId}
          />
        ) : (
        <ScrollArea className="h-[360px] -mx-1 px-1">
          <div className="space-y-2">
            {products.map((p) => {
              const up = p.delta > 0;
              const flat = p.delta === 0;
              const hist = (p.history || []);
              return (
                <div key={p.product_id} className="rounded-lg border p-3" style={{ borderColor: 'var(--border-default)' }}
                  data-testid={`po-price-change-row-${p.product_id}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-800 truncate">
                        {p.name}{p.sku ? <span className="text-xs font-normal text-slate-400"> ({p.sku})</span> : null}
                      </p>
                      <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-0.5 flex-wrap" style={{ fontVariantNumeric: 'tabular-nums' }}>
                        <span>{formatPHP(p.old_capital)}</span>
                        <ArrowRight size={11} className="text-slate-300" />
                        {/* New cost: show recorded (moving-avg) value. When the PO's unit
                            price differs (costing method picked MA over a cheaper PO) we
                            also surface the raw PO price so the operator sees the real
                            movement, not just the smoothed cost. */}
                        <span className="font-semibold text-slate-700">{formatPHP(p.new_capital)}</span>
                        <span className="text-[9px] text-slate-400 font-medium uppercase tracking-wide">MA</span>
                        {p.po_unit_price != null && Math.abs((Number(p.po_unit_price) || 0) - (Number(p.new_capital) || 0)) >= 0.005 && (
                          <>
                            <span className="text-slate-300">·</span>
                            <span className="text-slate-600">{formatPHP(p.po_unit_price)}</span>
                            <span className="text-[9px] text-slate-400 font-medium uppercase tracking-wide">PO</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className={`flex items-center gap-1 text-sm font-bold shrink-0 ${flat ? 'text-slate-400' : up ? 'text-red-600' : 'text-emerald-600'}`}
                      style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {!flat && (up ? <TrendingUp size={14} /> : <TrendingDown size={14} />)}
                      {p.delta > 0 ? '+' : ''}{formatPHP(p.delta)}
                    </div>
                  </div>
                  {hist.length > 0 && (
                    <div className="mt-2 pt-2 border-t flex flex-wrap items-center gap-1.5" style={{ borderColor: 'var(--border-default)' }}>
                      <span className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">Last 3 POs</span>
                      {hist.map((h, idx) => {
                        const ma = Number(h.new_capital) || 0;
                        const po = Number(h.po_unit_price) || 0;
                        const showBoth = po && Math.abs(ma - po) >= 0.005;
                        return (
                          <span key={idx} className="text-[11px] bg-slate-100 text-slate-600 rounded px-1.5 py-0.5" style={{ fontVariantNumeric: 'tabular-nums' }}
                            title={h.source_ref || ''}>
                            {fmtDate(h.changed_at)}{h.source_ref ? ` · ${h.source_ref}` : ''}:{' '}
                            {showBoth
                              ? <>
                                  <b>{formatPHP(ma)}</b><span className="text-[9px] text-slate-400">MA</span>
                                  {' · '}
                                  <b>{formatPHP(po)}</b><span className="text-[9px] text-slate-400">PO</span>
                                </>
                              : <b>{formatPHP(ma)}</b>}
                          </span>
                        );
                      })}
                    </div>
                  )}
                  {canPropagate && (
                    <div className="mt-2 flex justify-end">
                      <Button size="sm" variant="outline" className="h-7 text-[11px] text-[#1A4D2E] border-emerald-200 hover:bg-emerald-50"
                        onClick={() => setCompareProduct(p)}
                        data-testid={`compare-branches-${p.product_id}`}>
                        <GitBranch size={12} className="mr-1" /> Compare &amp; propagate to other branches
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </ScrollArea>
        )}

        <div className="flex flex-wrap gap-2 pt-1">
          <Button
            variant="outline"
            className="flex-1 min-w-[140px]"
            onClick={() => navigate('/capital-change-report')}
            data-testid="view-capital-report-btn"
          >
            <ExternalLink size={14} className="mr-1.5" /> Full Capital Report
          </Button>
          <Button
            className="flex-1 min-w-[140px] bg-[#1A4D2E] hover:bg-[#14532d] text-white"
            onClick={handlePrint}
            data-testid="print-price-change-report-btn"
          >
            <Printer size={14} className="mr-1.5" /> Print Price Change Report
          </Button>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="po-price-change-done-btn">
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>

    {compareProduct && (
      <BranchCapitalCompareModal
        open={!!compareProduct}
        onOpenChange={(v) => { if (!v) setCompareProduct(null); }}
        product={compareProduct}
        sourceBranchId={sourceBranchId}
        sourcePoNumber={report.po_number}
        sourceDeliveryDate={compareProduct?.delivery_date || ''}
      />
    )}
    </>
  );
}
